import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Alert, ActivityIndicator } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import { setItem } from '../utils/storage';
import { login } from '../services/bluesky';
import { Theme, useTheme, useThemedStyles } from '../theme';
import { useTranslation } from 'react-i18next';

interface LoginProps {
  onLoginSuccess: () => void;
  onCancel?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess, onCancel }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const handleLogin = async () => {
    if (!identifier || !password) {
      Alert.alert(t('common.error'), t('login.error_empty'));
      return;
    }

    setLoading(true);
    try {
      await login(identifier, password);
      onLoginSuccess();
    } catch (error: any) {
      console.error('handleLogin error:', error);
      try {
        Alert.alert(t('login.error'), error.message || t('login.error_failed'));
      } catch (e) {
        // Alert may not work on web; fallback to window.alert
        try { window.alert((error && error.message) || t('login.error_failed')); } catch {};
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('login.title')}</Text>
      <TextInput
        style={styles.input}
        placeholder={t('login.identifier')}
        value={identifier}
        onChangeText={setIdentifier}
        autoCapitalize="none"
        placeholderTextColor={theme.colors.textMuted}
      />
      <TextInput
        style={styles.input}
        placeholder={t('login.password')}
        value={password}
        onChangeText={setPassword}
        secureTextEntry
        placeholderTextColor={theme.colors.textMuted}
      />
      <TouchableOpacity style={styles.button} onPress={handleLogin} disabled={loading}>
        {loading ? (
          <ActivityIndicator color={theme.colors.primaryText} />
        ) : (
          <Text style={styles.buttonText}>{t('login.button')}</Text>
        )}
      </TouchableOpacity>
      {onCancel && (
        <TouchableOpacity style={styles.cancelButton} onPress={onCancel} disabled={loading}>
          <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    padding: 20,
    backgroundColor: theme.colors.background,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 20,
    textAlign: 'center',
  },
  input: {
    backgroundColor: theme.colors.input,
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
    color: theme.colors.text,
    borderWidth: 0.5,
    borderColor: theme.colors.border,
  },
  button: {
    backgroundColor: theme.colors.primary,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  buttonText: {
    color: theme.colors.primaryText,
    fontWeight: 'bold',
    fontSize: 16,
  },
  cancelButton: {
    marginTop: 15,
    padding: 15,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: theme.colors.text,
    fontSize: 16,
  },
});

export default Login;
